import { useEffect, useState } from "react";
import { getAllTeams, getTeamsByWorker } from "../services/teams";
import type { Database } from "../pages/types/supabase";

type Team = Database["public"]["Tables"]["teams"]["Row"];

interface TeamCache {
  data: Team[];
  timestamp: number;
}

interface WorkerTeamCache {
  [workerId: number]: {
    data: Team[];
    timestamp: number;
  };
}

// Simple cache with 5-minute TTL
const TEAM_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let allTeamsCache: TeamCache | null = null;
let workerTeamsCache: WorkerTeamCache = {};

function isValidCache(cache: TeamCache | null): boolean {
  if (!cache) return false;
  return Date.now() - cache.timestamp < TEAM_CACHE_TTL;
}

export function useAllTeams() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTeams() {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        if (isValidCache(allTeamsCache)) {
          setTeams(allTeamsCache!.data);
          setLoading(false);
          return;
        }

        // Fetch from API
        const teamsData = await getAllTeams();
        
        // Update cache
        allTeamsCache = {
          data: teamsData,
          timestamp: Date.now()
        };
        
        setTeams(teamsData);
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch teams");
      } finally {
        setLoading(false);
      }
    }

    fetchTeams();
  }, []);

  return { teams, loading, error };
}

export function useWorkerTeams(workerId: number | null) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchWorkerTeams() {
      if (!workerId) {
        setTeams([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cachedWorkerTeams = workerTeamsCache[workerId];
        if (cachedWorkerTeams && isValidCache(cachedWorkerTeams)) {
          setTeams(cachedWorkerTeams.data);
          setLoading(false);
          return;
        }

        // Fetch from API
        const teamsData = await getTeamsByWorker(workerId);
        
        // Update cache
        workerTeamsCache[workerId] = {
          data: teamsData,
          timestamp: Date.now()
        };
        
        setTeams(teamsData);
      } catch (err) {
        console.error("Error fetching worker teams:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch worker teams");
      } finally {
        setLoading(false);
      }
    }

    fetchWorkerTeams();
  }, [workerId]);

  return { teams, loading, error };
}

// Function to invalidate cache when teams are updated
export function invalidateTeamCache() {
  allTeamsCache = null;
  workerTeamsCache = {};
}